import { useState, useEffect } from 'react';
import { CURRENCIES, FALLBACK_RATES } from '../constants';
import { AdSlot, PageFooter } from '../components/UI';

function fxFmt(amount, code) {
  const c = CURRENCIES.find(x => x.code === code);
  const sym = c ? c.symbol : code + ' ';
  if (['JPY', 'KRW', 'IDR'].includes(code)) return sym + Math.round(amount).toLocaleString();
  return sym + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CurrencyConverter() {
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [loaded, setLoaded] = useState(false);
  const [offline, setOffline] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('EUR');
  const [amount, setAmount] = useState(1);

  useEffect(() => {
    const sources = [
      () => fetch('https://api.frankfurter.dev/v1/latest?from=USD').then(r => r.json()).then(d => ({ rates: { USD: 1, ...d.rates }, date: d.date })),
      () => fetch('https://api.frankfurter.app/latest?from=USD').then(r => r.json()).then(d => ({ rates: { USD: 1, ...d.rates }, date: d.date })),
      () => fetch('https://open.er-api.com/v6/latest/USD').then(r => r.json()).then(d => ({ rates: d.rates, date: d.time_last_update_utc?.slice(0, 10) || 'recent' })),
    ];
    (async () => {
      for (const source of sources) {
        try {
          const data = await Promise.race([source(), new Promise((_, rej) => setTimeout(() => rej(), 4000))]);
          if (data?.rates?.EUR) { setRates(data.rates); setLastUpdated(data.date || 'recently'); setLoaded(true); return; }
        } catch {}
      }
      setOffline(true); setLoaded(true);
    })();
  }, []);

  const getRate = (f, t) => {
    const inUSD = rates[f] ? (1 / rates[f]) : 1;
    return inUSD * (rates[t] || 1);
  };

  const rate = getRate(from, to);
  const converted = amount * rate;
  const fromC = CURRENCIES.find(x => x.code === from);
  const QUICK = [1, 5, 10, 50, 100, 500, 1000, 5000];

  return (
    <div>
      <div className="fx-hero">
        <div className="fx-hero-inner">
          <div className="fx-badge">Live Exchange Rates</div>
          <h1>Currency <em>Converter</em></h1>
          <p>Real-time rates from the European Central Bank. Updated daily.</p>
        </div>
      </div>

      <div className="fx-wrap">
        <AdSlot />
        <div className="fx-card">
          <div className="fx-main">
            <div className="fx-rate-badge">
              <div className="fx-rate-dot" />
              {offline ? 'Approximate rates · offline mode' : loaded ? `Live rates · ECB data` : 'Loading rates...'}
            </div>

            {!loaded ? (
              <div className="fx-loading"><div className="fx-spinner" /><div>Loading live exchange rates...</div></div>
            ) : (
              <>
                <div className="fx-inputs">
                  <div className="fx-field">
                    <label>From</label>
                    <div className="fx-input-row">
                      <input type="number" value={amount} min={0} step="any" onChange={e => setAmount(+e.target.value || 0)} />
                      <select className="fx-currency-select" value={from} onChange={e => setFrom(e.target.value)}>
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="fx-swap-col">
                    <button className="fx-swap-btn" onClick={() => { setFrom(to); setTo(from); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 16V4m0 0L3 8m4-4l4 4" /><path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </button>
                  </div>

                  <div className="fx-field">
                    <label>To</label>
                    <div className="fx-input-row">
                      <input type="text" readOnly value={['JPY','KRW','IDR'].includes(to) ? Math.round(converted).toLocaleString() : converted.toFixed(2)}
                        style={{ background: 'var(--bg)', color: 'var(--green)', fontWeight: 700, cursor: 'default' }} />
                      <select className="fx-currency-select" value={to} onChange={e => setTo(e.target.value)}>
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="fx-result-bar">
                  <div>
                    <div className="fx-result-label">{fxFmt(amount, from)} {fromC?.name}</div>
                    <div className="fx-result-amount">{fxFmt(converted, to)}</div>
                    <div className="fx-result-rate">1 {from} = {fxFmt(rate, to)} · 1 {to} = {fxFmt(1 / rate, from)}</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {loaded && (
            <>
              <div className="fx-quick">
                <div className="fx-quick-title">Quick Reference</div>
                <div className="fx-quick-grid">
                  {QUICK.map(q => (
                    <div key={q} className="fx-quick-item" onClick={() => setAmount(q)}>
                      <div className="fx-quick-flag">{fromC?.flag}</div>
                      <div className="fx-quick-currency">{from} {q.toLocaleString()}</div>
                      <div className="fx-quick-amount">{fxFmt(q * rate, to)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="fx-table-section">
                <AdSlot />
                <div className="fx-table-title">All Exchange Rates</div>
                <table className="fx-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Currency</th>
                      <th>Rate (1 {from})</th>
                      <th>Your Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CURRENCIES.filter(c => c.code !== from).map(c => {
                      const r = getRate(from, c.code);
                      return (
                        <tr key={c.code}>
                          <td className="td-flag">{c.flag}</td>
                          <td><div className="td-code">{c.code}</div><div className="td-name">{c.name}</div></td>
                          <td className="td-rate">{fxFmt(r, c.code)}</td>
                          <td className="td-converted">{fxFmt(amount * r, c.code)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fx-updated">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {offline ? 'Using approximate offline rates. Connect to the internet for live data.' : `Rates updated: ${lastUpdated} · Powered by Frankfurter (ECB data)`}
              </div>
            </>
          )}
        </div>
      </div>
      <PageFooter />
    </div>
  );
}
